import { useAnimations, useGLTF } from "@react-three/drei";
import { SceneProps, useThree } from "@react-three/fiber";
import React from "react";
import { useEffect, useRef } from "react";
import {
  BackSide,
  DoubleSide,
  FrontSide,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
} from "three";
import { ACTION_TRIGGERS } from "./ActionTriggers";
import { CustomMaterial } from "./CustomMaterial";
import { VideoAsset } from "./store/AssetSlice";
import { useStore } from "./store/Store";

export type GltfProps = {
  url: string;
  name: string;
  customMaterials?: CustomMaterial[];
  // see: https://enable3d.io/docs.html#basic-shapes
  physicsShape?:
    | "concave"
    | "convex"
    | "box"
    | "sphere"
    | "box"
    | "hull"
    | "hacd"
    | "convexMesh"
    | "concaveMesh"
    | "plane"
    | "torus"
    | "cylinder"
    | "capsule"
    | "vhacd"
    | "hacd"
    | "none";
  collisionFlag?: 0 | 1 | 2 | 4;
  singleRigidBody?: boolean;
  mass?: number;
  bloom?: "partial" | "full";
  clickable?: boolean;
};

const makeOccluder = (
  targetElement: Mesh,
  materialSide: "back" | "front" | "double" = "double"
) => {
  const customMat = new MeshStandardMaterial();
  customMat.colorWrite = false;
  targetElement.material = customMat;
  targetElement.renderOrder = -1;
  targetElement.castShadow = false;
  targetElement.receiveShadow = false;
  customMat.transparent = false;
  customMat.side =
    materialSide === "back"
      ? BackSide
      : materialSide === "front"
      ? FrontSide
      : DoubleSide;
};

const applyCustomMaterial = (
  targetElement: any,
  customMat: any,
  assets: any
) => {
  if (targetElement && (targetElement as Mesh).isMesh) {
    // Add video Texture
    if (
      customMat.videoSettings &&
      assets.get(customMat.videoSettings?.videoId)
    ) {
      const videoTexture = (
        assets.get(customMat.videoSettings?.videoId) as VideoAsset
      ).videoTexture;
      if ((targetElement as Mesh).material instanceof MeshBasicMaterial) {
        const dummyMat = new MeshStandardMaterial();
        dummyMat.map = videoTexture;

        (targetElement as Mesh).material = dummyMat;
      } else {
        ((targetElement as Mesh).material as MeshStandardMaterial).map =
          videoTexture;
      }

      videoTexture.flipY = false;
    }

    // Make occluder material
    if (customMat.occluder) {
      makeOccluder(targetElement as Mesh);
    }
  }
};

export const GltfComponent: React.FC<GltfProps & SceneProps> = (
  props: GltfProps & SceneProps
) => {
  const ref = useRef(null);
  const addAsset = useStore((store) => store.addAsset);
  const triggerAction = useStore().triggerAction;
  const assets = useStore((store) => store.assets);
  const gltf = useGLTF(props.url);
  const { clips, mixer } = useAnimations(gltf.animations, gltf.scene);
  const setSelectedObject = useStore((store) => store.setSelectedObject);

  const physics = useStore((store) => store.physics);

  useEffect(() => {
    if (!physics || props.physicsShape === "none") return;
    if (props.singleRigidBody) {
      physics?.add.existing(gltf.scene as any, {
        shape: props.physicsShape || "box",
        collisionFlags: props.collisionFlag || 0,
        mass: props.mass || 1,
      });
    } else {
      gltf.scene.traverse((mesh) => {
        if ((mesh as Mesh).isMesh) {
          physics?.add.existing(mesh as any, {
            shape: props.physicsShape || "box",
            collisionFlags: props.collisionFlag || 0,
            mass: props.mass || 1,
          });
        }
      });
    }
  }, [
    physics,
    gltf,
    props.singleRigidBody,
    props.physicsShape,
    props.collisionFlag,
    props.mass,
  ]);

  useEffect(() => {
    // Bloom has 3 states
    // bloom={"partial"} = Applies bloom to selected meshes
    // bloom={"full"} = Applies bloom to the whole model
    // bloom=not set = Don't apply bloom

    // set bloom setting to metadata
    // metadata will be evaluated inside bloomPipeline.ts

    if (props.bloom === "partial") {
      // Applies bloom to selected meshes
      gltf.scene.traverse((mesh) => {
        if ((mesh as Mesh).isMesh) {
          if (mesh.name.includes("_bloom")) {
            mesh.userData = Object.assign({}, mesh.userData, {
              bloom: true,
            });
          }
        }
      });
    } else if (props.bloom === "full") {
      // Don't apply bloom
      gltf.scene.traverse((mesh) => {
        if ((mesh as Mesh).isMesh) {
          mesh.userData = Object.assign({}, mesh.userData, { bloom: true });
        }
      });
    }
  }, [gltf, props.bloom]);

  useEffect(() => {
    addAsset({ gltf, animation: { clips, mixer } }, props.name);
    triggerAction(ACTION_TRIGGERS.MODEL_READY, props.name);

    const animationFinished = (event: any) => {
      triggerAction(
        ACTION_TRIGGERS.ANIMATION_ENDED,
        props.name,
        undefined,
        event
      );
    };
    mixer.addEventListener("finished", animationFinished);

    return () => {
      mixer.removeEventListener("finished", animationFinished);
    };
  }, [addAsset, clips, gltf, mixer, props.name, triggerAction]);

  useEffect(() => {
    if (props.customMaterials && props.customMaterials.length > 0 && gltf) {
      props.customMaterials.forEach((customMat) => {
        if (customMat.targetElement) {
          const targetElement = gltf.scene.getObjectByName(
            customMat.targetElement
          );

          applyCustomMaterial(targetElement, customMat, assets);
        } else if (customMat.targetNaming) {
          gltf.scene.traverse((mesh: any) => {
            if (mesh.name.includes(customMat.targetNaming)) {
              applyCustomMaterial(mesh, customMat, assets);
            }
          });
        }
      });
    }
  }, [props.customMaterials, gltf, assets]);

  return (
    <primitive
      ref={ref}
      {...props}
      name={props.name}
      object={gltf.scene}
      onClick={(event: any) => {
        if (!props.clickable) return;
        event.stopPropagation();
        console.log("event", event, gltf.scene);
        triggerAction(ACTION_TRIGGERS.TAP, props.name, event.object.name, {
          physics,
          gltf,
        });
      }}
      onPointerDown={() => {
        // console.log("setSelected", ref.current);
        setSelectedObject?.(ref.current as any);
      }}
    ></primitive>
  );
};
